import { CommonModule } from '@angular/common'
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    model,
    output,
    signal
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { APP_CONFIG } from '@isl-core/constants/app.constant'
import { UserRolesEnum } from '@isl-core/enums/user-roles.enum'
import { UserInterface } from '@isl-core/interfaces/user.interface'
import { AppDataService } from '@isl-core/services/app-data.service'
import { DataService } from '@isl-core/services/data.service'
import { RouteService } from '@isl-core/services/route.service'
import { UserService } from '@isl-core/services/user.service'
import { LabourSheetCounterInterface } from '@isl-shared/interfaces/app.interface'
import { getLabourSheetStatus } from '@isl-utils/helper.util'
import { MenuItem } from 'primeng/api'
import { ButtonModule } from 'primeng/button'
import { Select, SelectChangeEvent } from 'primeng/select'
import { TooltipModule } from 'primeng/tooltip'

@Component({
    selector: 'app-header',
    standalone: true,
    imports: [CommonModule, ButtonModule, Select, FormsModule, TooltipModule],
    templateUrl: './header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
    user = input<UserInterface | null>(null)
    options = input<MenuItem[]>([])
    userOptions = input<MenuItem[]>([])

    city = model('')
    userRoles = model<string[]>([])

    menu = signal<MenuItem[]>([])

    logoutEvent = output<boolean>()

    labourSheetCounters: LabourSheetCounterInterface[] | null = null

    @HostListener('document:mousedown', ['$event'])
    onGlobalClick(event: Event): void {
        const target = event.target as HTMLElement
        const clickedInside = this.elementRef.nativeElement.contains(event.target)
        if (!clickedInside || !target.classList.contains('user-dropdown-menu')) {
            this.isMenuOpen.set(false)
        }
    }

    isMenuOpen = signal<boolean>(false)
    isMobileMenuOpen = signal<boolean>(false)
    selectOptions = signal(APP_CONFIG.cityOptions.filter((option) => option.enabled))
    selectedOption = signal<{ label: string; value: string } | undefined>(undefined)
    menuActive = signal<number>(0)
    isCreateButtonVisible = signal<boolean>(false)
    isRestrictedForeman = signal<boolean>(false)

    private router = inject(Router)
    private elementRef = inject(ElementRef)
    private appDataService = inject(AppDataService)
    private routeService = inject(RouteService)
    private userService = inject(UserService)
    private dataService = inject(DataService)

    constructor() {
        this.isCreateButtonVisible.set(this.userService.hasAnyRole([UserRolesEnum.LabourOffice, UserRolesEnum.Payroll]))
        this.isRestrictedForeman.set(this.userService.hasAnyRole([UserRolesEnum.RestrictedForeman]))

        if (
            this.userService.hasAnyRole([
                UserRolesEnum.RestrictedForeman,
                UserRolesEnum.Foreman,
                UserRolesEnum.LabourOffice
            ])
        ) {
            this.selectOptions.set(APP_CONFIG.cityOptionsForeman)
        }

        effect(() => {
            const labourSheetCounters = this.appDataService.labourSheetCounters()()
            let menuActive = 0

            if (labourSheetCounters) {
                this.labourSheetCounters = this.appDataService.labourSheetCounters()()
                this.addCounterToMenu()
            }

            this.selectedOption.set({
                label: this.city(),
                value: this.city()
            })

            const routeData = this.routeService.getData()()
            if (routeData && routeData['menuActive']) {
                menuActive = parseInt(routeData['menuActive'])
            }

            this.menuActive.set(menuActive)

            this.addActiveToMenu()
        })
    }

    goHome() {
        this.router.navigate(['/'])
        this.isMenuOpen.set(false)
    }

    goTo(url: string) {
        if (this.isRestrictedForeman() && url === '/reports') return
        this.router.navigate([url])
        this.isMenuOpen.set(false)
        this.isMobileMenuOpen.set(false)
    }

    addDraft(): void {
        this.router.navigate(['/labourSheets/add'])
    }

    logout() {
        this.logoutEvent.emit(true)
        this.isMenuOpen.set(false)
    }

    changeOption(event: SelectChangeEvent): void {
        this.city.set(event.value)
        this.router.navigate(['/reports'])
        this.dataService.refreshLabourSheetCounters()
    }

    addCounterToMenu(): void {
        if (!this.labourSheetCounters) return

        this.options().map((menuItem) => {
            if (menuItem.state) {
                menuItem.state['badgeCount'] =
                    this.labourSheetCounters
                        ?.find((counter) => counter.statusName === menuItem.label?.toLowerCase())
                        ?.count.toString() || '0'
            }
        })
    }

    addActiveToMenu(): void {
        const menu = [...this.options()]

        menu.map((menuItem) => {
            const label = menuItem.label

            if (!menuItem.state) {
                menuItem.state = {}
            }

            if (!menuItem.state['active']) {
                menuItem.state['active'] = false
            }

            menuItem.state['active'] = false

            if (this.menuActive() === getLabourSheetStatus(label || '')) {
                menuItem.state['active'] = true
            }
        })

        this.menu.set(menu)
    }
}
